import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import ArtifactChecklist from "@/components/evidenceGathering/ArtifactChecklist";
import { isFromLatestUpload, latestUploadVersion } from "@/lib/artifacts";
import { findArtefacts, recordArtefactEvents } from "@/lib/evidenceAudit";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useActivateArtifacts, useDeactivateArtifacts, useDeleteArtifacts } from "@/hooks/useProjects";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactCatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All artifacts ever extracted for this project, active and inactive. */
  artifacts: Artifact[];
  projectId: string;
}

// Reselect previously-extracted artifacts without re-uploading the CEM file —
// mirrors EvidenceCatalogDialog's toAdd/toRemove diff-and-mutate-in-parallel
// pattern, just keyed by uid (activate/deactivate) instead of document id
// (insert/delete).
const ArtifactCatalogDialog = ({ open, onOpenChange, artifacts, projectId }: ArtifactCatalogDialogProps) => {
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deletingUids, setDeletingUids] = useState<Set<string>>(new Set());
  const activateArtifacts = useActivateArtifacts();
  const deactivateArtifacts = useDeactivateArtifacts();
  const deleteArtifacts = useDeleteArtifacts();

  // A persisted artefact carries its version but not whether the newest upload
  // is what put it there — the preview API decides that for upload rows, and
  // there is no upload happening here. Derived from the project's own highest
  // version instead, so this table highlights the same rows the cards do.
  const rows = useMemo(() => {
    const latest = latestUploadVersion(artifacts);
    return artifacts.map((a) => ({ ...a, changed: isFromLatestUpload(a, latest) }));
  }, [artifacts]);

  const activeUids = artifacts.filter((a) => a.isActive).map((a) => a.uid);

  useEffect(() => {
    if (open) {
      setSelectedUids(activeUids);
    } else {
      setSelectedUids([]);
      activateArtifacts.reset();
      deactivateArtifacts.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Deleting is immediate rather than staged with the tick changes: it's
  // irreversible, and mixing it into "Update Artifacts" would mean one button
  // doing both a reversible and an unrecoverable thing.
  const handleDelete = () => {
    if (!pendingDelete) return;
    const uid = pendingDelete;
    setDeletingUids((prev) => new Set(prev).add(uid));
    // mutateAsync rather than per-call callbacks: those only fire for the latest of
    // overlapping calls, and a second delete can start before the first returns.
    deleteArtifacts
      .mutateAsync({ projectId, uids: [uid] })
      .then(() => {
        recordArtefactEvents(projectId, "delete_artefact", findArtefacts([uid], artifacts));
        setSelectedUids((prev) => prev.filter((id) => id !== uid));
        setPendingDelete(null);
      })
      .catch(() => {
        // Surfaced via deleteArtifacts.isError.
      })
      .finally(() =>
        setDeletingUids((prev) => {
          const next = new Set(prev);
          next.delete(uid);
          return next;
        })
      );
  };

  const toActivate = selectedUids.filter((uid) => !activeUids.includes(uid));
  const toDeactivate = activeUids.filter((uid) => !selectedUids.includes(uid));
  const hasChanges = toActivate.length > 0 || toDeactivate.length > 0;
  const isSaving = activateArtifacts.isPending || deactivateArtifacts.isPending;

  const handleSave = () => {
    if (!hasChanges) return;

    const tasks: Promise<unknown>[] = [];
    if (toActivate.length > 0) {
      tasks.push(
        activateArtifacts
          .mutateAsync({ projectId, uids: toActivate })
          .then(() => recordArtefactEvents(projectId, "add_artefact", findArtefacts(toActivate, artifacts)))
      );
    }
    if (toDeactivate.length > 0) {
      tasks.push(
        deactivateArtifacts
          .mutateAsync({ projectId, uids: toDeactivate })
          .then(() => recordArtefactEvents(projectId, "remove_artefact", findArtefacts(toDeactivate, artifacts)))
      );
    }

    Promise.all(tasks)
      .then(() => {
        setSelectedUids([]);
        onOpenChange(false);
      })
      .catch(() => {
        // Errors surface via activateArtifacts.isError / deactivateArtifacts.isError below; keep the dialog open.
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Select from Extracted Artefacts</DialogTitle>
          <DialogDescription>
            Choose which previously-extracted artefacts should be active for this project — no re-upload needed.
            To add an artefact that isn't listed here, upload the CEM file again.
          </DialogDescription>
        </DialogHeader>

        {artifacts.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No artefacts have been extracted yet. Upload a CEM file instead.
          </div>
        ) : (
          <ArtifactChecklist
            items={rows}
            selectedUids={selectedUids}
            onSelectionChange={setSelectedUids}
            onDelete={(item) => setPendingDelete(item.uid)}
            deletingUids={deletingUids}
          />
        )}

        {(activateArtifacts.isError || deactivateArtifacts.isError || deleteArtifacts.isError) && (
          <p className="text-sm text-destructive">
            Failed to update artefacts:{" "}
            {(activateArtifacts.error ?? deactivateArtifacts.error ?? deleteArtifacts.error)?.message}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="brand"
            disabled={!hasChanges || isSaving}
            onClick={handleSave}
            className="gap-1.5"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Update Artefacts
          </Button>
        </DialogFooter>
      </DialogContent>

      <DeleteConfirmationDialog
        open={pendingDelete !== null}
        title="Delete artefact"
        description="This removes the artefact from the project entirely. To get it back you'll need to upload the CEM file again and re-select it."
        itemName={artifacts.find((a) => a.uid === pendingDelete)?.name}
        isDeleting={pendingDelete !== null && deletingUids.has(pendingDelete)}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Dialog>
  );
};

export default ArtifactCatalogDialog;
